// Show a continued conversation after a reload (TOM-252).
//
// The chat keeps its conversation id across a reload (neoChatResume.ts) and the
// server continues the thread, so the model sees the earlier turns. The panel
// has to show them too: a chat that opens on its bare greeting while Neomaris
// answers "as you said earlier" tells the owner something false about what it
// remembers. So when the chat resumes a conversation it reads that
// conversation's messages once (GET /v1/neo/conversations/:id/messages) and
// shows them in place of the greeting.
//
// The rule: the chat never continues a conversation the owner cannot see. When
// the server no longer knows the conversation (`404 conversation_not_found`:
// deleted, another account's, bound to another boat), the stored id is cleared
// and the chat starts fresh, as the chat routes' 404 already did. Any other
// failure to show it — a network error, a server error, a body the chat cannot
// read — does the same, at the cost of starting the next summary early.
//
// What each message looks like is decided here, the way the chat shows it while
// the conversation is live: the owner's words as their bubble; an answer as
// Neomaris's, with its charts, maps and diagrams, its thumbs and the rating it holds; a note the
// server wrote (an alarm change, ADR 0210; a Watch update, ADR 0198) as a
// Neomaris line with no thumbs, like the chip the chat showed for the change;
// Neomaris's message about one of the boat's alarms (ADR 0337) also keeps the
// alarm it is about, so the chat links it there (TOM-551). An owner's Undo of
// Neo's change (TOM-662) is a small plain line, as the live chat drew it.
//
// Pure, so cloud/web's Node vitest covers it (no DOM harness).

import { parseNeoAnswer, type NeoAnswerRecord } from '@neomaris/protocol';

import {
  parseLabelChanges,
  parseLabelRequests,
  type NeoLabelChange,
  type NeoLabelChangeRequest,
} from './neoNames';
import { parseChatAttachments, type UploadedChatAttachment } from './chatAttachments';
import { parseBoardChanges, type NeoBoardChange } from './neoBoard';
import type { NeoAnswerChart } from '@neomaris/protocol';

import { parseNeoChatCharts } from './neoAnswerCharts';
import { parseNeoAnswerCards, type NeoAnswerCard } from './neoAnswerCards';
import { parseLookChanges, type NeoLookReceiptChange } from './neoLooksReceipt';
import {
  parseDataAnswerChanges,
  parseNotebookChanges,
  type NeoDataAnswerChange,
  type NeoNotebookRecord,
} from './neoReceipt';

/** The alarm a Neomaris alarm message is about (contract §12.11, TOM-551). */
export interface NeoAlarmLink {
  alertId: string;
  occurrenceNumber: number;
  event: 'sounded' | 'cleared';
}

/** One message of a continued conversation, in the chat's own message shape. */
export interface NeoHistoryMessage {
  /** The server's message id, as the chat's key. */
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** A note the server wrote: shown, never rated. */
  local?: true;
  /**
   * An owner's Undo of Neo's change (origin `owner_undo`, TOM-662): drawn as a
   * small plain line, not as a message from Neo.
   */
  undoNote?: true;
  /** An answered turn's id: thumbs and chart pins name it (ADR 0119, ADR 0230). */
  messageId?: string;
  /** The owner's own rating of that answer, as stored. */
  feedback?: 'up' | 'down';
  /** On Neomaris's message about an alarm: the alarm, to link to (TOM-551). */
  alarm?: NeoAlarmLink;
  /**
   * Neo's greeting on the boat's home (origin `greeting`, TOM-920): drawn as
   * an answer, with its questions as chips, but never rated.
   */
  greeting?: true;
  /** The charts the answer showed (ADR 0230). */
  charts?: NeoAnswerChart[];
  /**
   * The maps (TOM-468), diagrams (TOM-643) and pictures (TOM-646) it showed;
   * the web draws no other card kind.
   */
  cards?: NeoAnswerCard[];
  /**
   * The answer's record (TOM-709, contract §2): its source line and its
   * follow-ups. Absent for an answer stored without one.
   */
  answer?: NeoAnswerRecord;
  labelRequests?: NeoLabelChangeRequest[];
  /**
   * What the turn changed on the boat's boards (TOM-354, TOM-376): the
   * receipt under the answer, as the live chat drew it.
   */
  receipt?: {
    boards: NeoBoardChange[];
    answers?: NeoDataAnswerChange[];
    labels?: NeoLabelChange[];
    /** The notes the turn changed in the boat's notebook (TOM-611). */
    notebook?: NeoNotebookRecord[];
  };
  /**
   * What the turn changed in the owner's own panel looks (TOM-595): a private
   * receipt of its own under the answer, as the live chat drew it.
   */
  lookChanges?: NeoLookReceiptChange[];
  /** On an owner message: the photos and PDFs it carried (TOM-671, §12.11). */
  attachments?: UploadedChatAttachment[];
}

/** How a read of the conversation ended: its body, or the status it failed with. */
export type NeoHistoryRead =
  | { ok: true; body: unknown }
  | { ok: false; status: number | undefined; body: unknown };

/** What the chat does with a continued conversation once it has been read. */
export type NeoHistoryOutcome =
  /** Show these messages (none: keep the greeting) and keep the conversation. */
  | { kind: 'show'; messages: NeoHistoryMessage[] }
  /**
   * Clear the stored id and start a new conversation. `gone`: the server no
   * longer knows it. `unreadable`: it could not be shown.
   */
  | { kind: 'forget'; reason: 'gone' | 'unreadable' };

/** The read of one conversation's messages, in the boat scope it was started in. */
export function neoConversationMessagesUrl(conversationId: string, boatId?: string): string {
  const path = `/v1/neo/conversations/${encodeURIComponent(conversationId)}/messages`;
  return boatId === undefined ? path : `${path}?boatId=${encodeURIComponent(boatId)}`;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** An alarm message's `alarm`, or undefined when it is missing or malformed: then no link. */
export function parseNeoAlarmLink(value: unknown): NeoAlarmLink | undefined {
  if (!isObject(value)) return undefined;
  const { alertId, occurrenceNumber, event } = value;
  if (typeof alertId !== 'string' || alertId.length === 0) return undefined;
  if (typeof occurrenceNumber !== 'number' || !Number.isInteger(occurrenceNumber)) return undefined;
  if (occurrenceNumber < 0) return undefined;
  if (event !== 'sounded' && event !== 'cleared') return undefined;
  return { alertId, occurrenceNumber, event };
}

/** One message as the chat shows it, or null when it is not one the route sends. */
function toHistoryMessage(raw: unknown, boatId: string | undefined): NeoHistoryMessage | null {
  if (!isObject(raw)) return null;
  const { id, role, content, origin } = raw;
  if (typeof id !== 'string' || id.length === 0) return null;
  if (role !== 'user' && role !== 'assistant') return null;
  if (typeof content !== 'string') return null;
  if (origin !== null && origin !== undefined && typeof origin !== 'string') return null;
  if (role === 'user') {
    const attachments = parseChatAttachments(raw.attachments);
    return attachments.length > 0 ? { id, role, content, attachments } : { id, role, content };
  }

  // On a boat page a chart or a card of another boat is dropped, as the live
  // chat does.
  // `charts` and the answer charts (TOM-711) together.
  const charts = parseNeoChatCharts(raw.charts, raw.answerCharts, boatId);
  const cards = parseNeoAnswerCards(raw.cards, boatId);
  const shown = {
    ...(charts.length > 0 ? { charts } : {}),
    ...(cards.length > 0 ? { cards } : {}),
  };
  // Neo's greeting (TOM-920): an answer of its own, whose record carries the
  // questions it offers as chips. Written by the server, so never rated.
  if (origin === 'greeting') {
    const answer = parseNeoAnswer(raw.answer);
    return {
      id,
      role,
      content,
      messageId: id,
      greeting: true,
      ...shown,
      ...(answer === null ? {} : { answer }),
    };
  }
  if (origin !== null && origin !== undefined) {
    // A note the server wrote, not an answer the owner can rate. An alarm
    // message keeps the alarm it is about (TOM-551).
    const alarm = origin === 'alarm' ? parseNeoAlarmLink(raw.alarm) : undefined;
    return {
      id,
      role,
      content,
      local: true,
      ...(origin === 'owner_undo' ? { undoNote: true as const } : {}),
      ...shown,
      ...(alarm === undefined ? {} : { alarm }),
    };
  }
  const feedback = raw.feedback === 'up' || raw.feedback === 'down' ? raw.feedback : undefined;
  // A record this client cannot read is no record: the answer shows as before.
  const answer = parseNeoAnswer(raw.answer);
  return {
    id,
    role,
    content,
    messageId: id,
    ...(feedback === undefined ? {} : { feedback }),
    ...shown,
    ...(answer === null ? {} : { answer }),
  };
}

/**
 * The messages of the route's 200 body, in the order sent, or null when the
 * body is not for `conversationId`. A single malformed message is dropped.
 */
export function parseNeoConversationMessages(
  body: unknown,
  conversationId: string,
  boatId?: string
): NeoHistoryMessage[] | null {
  if (!isObject(body) || body.conversationId !== conversationId) return null;
  if (!Array.isArray(body.messages)) return null;
  const out: NeoHistoryMessage[] = [];
  for (const raw of body.messages) {
    const message = toHistoryMessage(raw, boatId);
    if (message === null) continue;
    out.push(message);
    const requests =
      message.role === 'assistant' && isObject(raw)
        ? parseLabelRequests(raw.labelRequests, boatId)
        : [];
    if (requests.length > 0)
      out.push({
        id: `${message.id}-label-requests`,
        role: 'assistant',
        content: '',
        local: true,
        labelRequests: requests,
      });
    const receipt = message.role === 'assistant' ? receiptOf(raw, boatId) : null;
    if (receipt !== null) {
      // Its own line under the answer, keyed as the live chat keys it.
      out.push({
        id: `${message.id}-receipt`,
        role: 'assistant',
        content: '',
        local: true,
        receipt,
      });
    }
    // The owner's own looks the turn changed (TOM-592 keeps them on the run),
    // on a boat page only this boat's, as the live chat draws them.
    const looks =
      message.role === 'assistant' && isObject(raw)
        ? parseLookChanges(raw.looks).filter((c) => boatId === undefined || c.boatId === boatId)
        : [];
    if (looks.length > 0) {
      out.push({
        id: `${message.id}-looks`,
        role: 'assistant',
        content: '',
        local: true,
        lookChanges: looks,
      });
    }
  }
  return out;
}

/**
 * The turn's records an answer carries (`board`, TOM-376; `dataAnswers`,
 * TOM-409; `labelChanges`), or
 * null when it carries none. On a boat page a record of another boat is
 * dropped, as the live chat's decoders do.
 */
function receiptOf(
  raw: unknown,
  boatId: string | undefined
): NonNullable<NeoHistoryMessage['receipt']> | null {
  if (!isObject(raw)) return null;
  const ours = <T extends { boatId: string }>(list: T[]) =>
    boatId === undefined ? list : list.filter((r) => r.boatId === boatId);
  const boards = ours(parseBoardChanges(raw.board));
  const answers = ours(parseDataAnswerChanges(raw.dataAnswers));
  const labels = parseLabelChanges(raw.labelChanges, boatId);
  // The boat's notebook (TOM-611).
  const notebook = ours(parseNotebookChanges(raw.notebook));
  return boards.length === 0 && answers.length === 0 && labels.length === 0 && notebook.length === 0
    ? null
    : {
        boards,
        answers,
        ...(labels.length > 0 ? { labels } : {}),
        ...(notebook.length > 0 ? { notebook } : {}),
      };
}

/** What to do with a continued conversation, from how its read ended. */
export function neoHistoryOutcome(
  read: NeoHistoryRead,
  conversationId: string,
  boatId?: string
): NeoHistoryOutcome {
  if (!read.ok) {
    const gone =
      read.status === 404 && isObject(read.body) && read.body.error === 'conversation_not_found';
    return { kind: 'forget', reason: gone ? 'gone' : 'unreadable' };
  }
  const messages = parseNeoConversationMessages(read.body, conversationId, boatId);
  if (messages === null) return { kind: 'forget', reason: 'unreadable' };
  return { kind: 'show', messages };
}

/**
 * The chat's messages once the continued conversation has arrived: its
 * messages in place of the bare greeting. With no messages, or once the chat
 * holds anything but local lines, it is left as it is (the chat sends nothing
 * while the read is in flight, so that is only a guard).
 */
export function withNeoHistory<M extends { local?: boolean }>(
  current: readonly M[],
  history: readonly M[]
): M[] {
  if (history.length === 0) return [...current];
  if (current.some((message) => message.local !== true)) return [...current];
  return [...history];
}

/**
 * The chat's messages once the main chat has been read again because a
 * message arrived from elsewhere: an alarm Neomaris posted, or a turn from
 * another tab (TOM-517). What came after the last stored message the chat
 * already holds is added under it; the chat's own lines stay as they are. A
 * chat holding none of the stored messages takes them as `withNeoHistory`
 * does, or, holding lines of its own, adds only the server's notes it lacks.
 */
export function withLaterNeoHistory<M extends { id: string; local?: boolean; messageId?: string }>(
  current: readonly M[],
  history: readonly M[]
): M[] {
  const held = new Set<string>();
  for (const message of current) {
    held.add(message.id);
    if (message.messageId !== undefined) held.add(message.messageId);
  }
  let last = -1;
  history.forEach((message, index) => {
    if (held.has(message.id)) last = index;
  });
  if (last === -1) {
    if (current.every((message) => message.local === true)) return withNeoHistory(current, history);
    return [...current, ...history.filter((m) => m.local === true && !held.has(m.id))];
  }
  return [...current, ...history.slice(last + 1).filter((m) => !held.has(m.id))];
}
